import React, { forwardRef, useState } from 'react';
import { PocIcon } from 'commons/components/components-wrapper/poc-icon.component';
import { CollapsibleCardTitleTooltipedTextStyled, StyledExpandable } from './collapsible-card.style';

export interface CollapsibleCardProps extends React.PropsWithChildren {
    title: string;
    isTitlePlaceholder?: boolean;
    titleTestId?: string;
    headerSuffix?: React.ReactNode;
    expanded?: boolean;
    disabled?: boolean;
    className?: string;
    'data-testid'?: string;
    onExpandedChange?: (expanded: boolean) => void;
}

export const CollapsibleCard = forwardRef<HTMLOwcExpandableElement, CollapsibleCardProps>(
    (
        {
            title,
            isTitlePlaceholder,
            titleTestId = 'collapsible-card-title',
            headerSuffix,
            expanded,
            disabled = false,
            onExpandedChange,
            children,
            ...rest
        },
        ref
    ) => {
        const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
        const isExpanded = expanded ?? uncontrolledExpanded;

        const handleExpandedChange = (ev: CustomEvent<boolean>) => {
            setUncontrolledExpanded(ev.detail);
            onExpandedChange?.(ev.detail);
        };

        return (
            <StyledExpandable
                ref={ref}
                variant="filled"
                controlPlacement="right"
                expanded={expanded}
                disabled={disabled}
                onExpandedChange={handleExpandedChange}
                {...rest}
            >
                <PocIcon slot="control" name={isExpanded ? 'chevron_up' : 'chevron_down'} family="outlined" />
                <span slot="title" data-testid={titleTestId}>
                    <CollapsibleCardTitleTooltipedTextStyled
                        $isPlaceholder={isTitlePlaceholder}
                        i18nKey="<tooltip>{{title}}</tooltip>"
                        tooltipText={title}
                        i18nValues={{ title }}
                    />
                </span>
                {headerSuffix ? <span slot="badge">{headerSuffix}</span> : undefined}
                <div slot="content">{children}</div>
            </StyledExpandable>
        );
    }
);

CollapsibleCard.displayName = 'CollapsibleCard';
